import React, { useState, useEffect } from 'react';
import { CategoryKPI, CreateCategoryKPIRequest } from '../../services/categoryKPIService';
import { useTheme } from '../../contexts/ThemeContext';
import { KPI_TIME_TYPES, VALID_KPI_TIME_TYPES } from '../../constants/kpiTimeTypes';
import './KPIManagementModal.css';

interface KPIManagementModalProps {
  isOpen: boolean;
  onClose: () => void;
  categoryId: number;
  categoryName: string;
  kpis: CategoryKPI[];
  measurementTypes: string[];
  onCreateKPI: (kpiData: CreateCategoryKPIRequest) => Promise<void>;
  onUpdateKPI: (id: number, kpiData: Partial<CategoryKPI>) => Promise<void>;
  onDeleteKPI: (id: number, name: string) => Promise<void>;
  loading?: boolean;
}

const KPIManagementModal: React.FC<KPIManagementModalProps> = ({
  isOpen,
  onClose,
  categoryId,
  categoryName,
  kpis,
  measurementTypes,
  onCreateKPI,
  onUpdateKPI,
  onDeleteKPI,
  loading = false
}) => {
  const { theme } = useTheme();
  const [isCreating, setIsCreating] = useState(true); // Start in create mode
  const [editingKPI, setEditingKPI] = useState<CategoryKPI | null>(null);
  const [formData, setFormData] = useState({
    kpiName: '',
    kpiCode: '',
    measurementType: '',
    description: '',
    type: 'Monthly Values'
  });
  const [saving, setSaving] = useState(false);

  // Reset form when modal opens/closes
  useEffect(() => {
    if (!isOpen) {
      setIsCreating(true);
      setEditingKPI(null);
      resetForm();
    }
  }, [isOpen]);

  // Debug: Log KPI updates
  useEffect(() => {
    if (isOpen) {
      console.log('KPIs updated in modal:', kpis);
      console.log('KPIs length in modal:', kpis.length);
      console.log('Loading state:', loading);
    }
  }, [kpis, loading, isOpen]);

  // Reset form when switching between create/edit
  useEffect(() => {
    if (editingKPI) {
      setFormData({
        kpiName: editingKPI.kpiName,
        kpiCode: editingKPI.kpiCode,
        measurementType: editingKPI.measurementType,
        description: editingKPI.description || '',
        type: editingKPI.type || 'Monthly Values'
      });
      setIsCreating(false);
    }
  }, [editingKPI]);

  const resetForm = () => {
    setFormData({
      kpiName: '',
      kpiCode: '',
      measurementType: '',
      description: '',
      type: 'Monthly Values'
    });
  };

  const generateKPICode = (name: string) => {
    return name
      .toUpperCase()
      .replace(/[^A-Z0-9\s]/g, '')
      .replace(/\s+/g, '_')
      .substring(0, 20);
  };

  const handleInputChange = (field: string, value: string) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));

    // Auto-generate KPI code when name changes
    if (field === 'kpiName' && !editingKPI) {
      const generatedCode = generateKPICode(value);
      setFormData(prev => ({
        ...prev,
        kpiCode: generatedCode
      }));
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!formData.kpiName.trim() || !formData.measurementType || !formData.type) {
      return;
    }

    try {
      setSaving(true);

      if (editingKPI) {
        // Update existing KPI
        await onUpdateKPI(editingKPI.id!, {
          kpiName: formData.kpiName.trim(),
          kpiCode: formData.kpiCode.trim().toUpperCase(),
          measurementType: formData.measurementType,
          description: formData.description.trim(),
          type: formData.type
        });
        setEditingKPI(null);
        setIsCreating(true);
      } else {
        // Create new KPI
        await onCreateKPI({
          categoryId,
          kpiName: formData.kpiName.trim(),
          kpiCode: formData.kpiCode.trim().toUpperCase(),
          measurementType: formData.measurementType,
          description: formData.description.trim(),
          type: formData.type,
          displayOrder: kpis.length
        });
      }

      resetForm();
    } catch (error) {
      // Error handling is done in parent component
    } finally {
      setSaving(false);
    }
  };

  const handleEdit = (kpi: CategoryKPI) => {
    setEditingKPI(kpi);
    setIsCreating(false);
  };

  const handleDelete = async (kpi: CategoryKPI) => {
    if (window.confirm(`Are you sure you want to delete "${kpi.kpiName}"?`)) {
      await onDeleteKPI(kpi.id!, kpi.kpiName);
    }
  };

  const handleCancel = () => {
    setIsCreating(true);
    setEditingKPI(null);
    resetForm();
  };

  const handleBackToList = () => {
    setIsCreating(false);
    setEditingKPI(null);
    resetForm();
  };

  if (!isOpen) return null;

  return (
    <div className="modal-overlay">
      <div className={`kpi-modal theme-${theme}`}>
        <div className="modal-header">
          <div className="modal-title">
            <h2>▶ {categoryName} KPIs</h2>
            <p className="modal-subtitle">Manage Key Performance Indicators</p>
          </div>
          <button className="modal-close" onClick={onClose}>✕</button>
        </div>

        <div className="content-layout">
            {/* KPI List Section */}
            <div className="kpi-list-section">
              <div className="section-header">
                <h3>Current KPIs</h3>
              </div>

              {loading ? (
                <div className="loading-state">Loading KPIs...</div>
              ) : kpis.length === 0 ? (
                <div className="empty-state">
                  <div className="empty-icon">📊</div>
                  <p>No KPIs defined for this category</p>
                  <p className="empty-subtitle">Use the form to create your first KPI</p>
                </div>
              ) : (
                <div style={{display: 'flex', flexDirection: 'column', gap: '1rem'}}>
                  {kpis.map((kpi, index) => (
                    <div key={kpi.id || index} style={{
                      background: 'rgba(64, 224, 255, 0.1)',
                      border: '1px solid rgba(64, 224, 255, 0.3)',
                      borderRadius: '8px',
                      padding: '15px',
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'flex-start'
                    }}>
                      <div style={{flex: 1}}>
                        <div style={{
                          fontWeight: 'bold',
                          fontSize: '1.1rem',
                          color: '#40e0ff',
                          marginBottom: '5px'
                        }}>
                          {kpi.kpiName}
                        </div>
                        <div style={{
                          color: 'rgba(255, 255, 255, 0.7)',
                          fontSize: '0.9rem',
                          marginBottom: '5px'
                        }}>
                          Type: {kpi.measurementType} | Period: {kpi.type} ({kpi.abbreviation})
                        </div>
                        {kpi.description && (
                          <div style={{
                            color: 'rgba(255, 255, 255, 0.6)',
                            fontSize: '0.85rem'
                          }}>
                            {kpi.description}
                          </div>
                        )}
                      </div>
                      <div style={{display: 'flex', gap: '8px', marginLeft: '15px'}}>
                        <button 
                          onClick={() => handleEdit(kpi)}
                          disabled={saving}
                          style={{
                            background: '#40e0ff',
                            color: 'white',
                            border: 'none',
                            borderRadius: '4px',
                            padding: '6px 12px',
                            cursor: 'pointer',
                            fontSize: '0.85rem'
                          }}
                        >
                          ✏️ Edit
                        </button>
                        <button 
                          onClick={() => handleDelete(kpi)}
                          disabled={saving}
                          style={{
                            background: '#ff6b6b',
                            color: 'white',
                            border: 'none',
                            borderRadius: '4px',
                            padding: '6px 12px',
                            cursor: 'pointer',
                            fontSize: '0.85rem'
                          }}
                        >
                          🗑️ Delete
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Form Section */}
            <div className="form-section">
              <div className="form-header">
                <h3>{editingKPI ? 'Edit KPI' : 'Create New KPI'}</h3>
              </div>

              <form onSubmit={handleSubmit} className="kpi-form">
                <div className="form-group">
                  <label htmlFor="kpiName">KPI Name *</label>
                  <input
                    id="kpiName"
                    type="text"
                    value={formData.kpiName}
                    onChange={(e) => handleInputChange('kpiName', e.target.value)}
                    placeholder="Enter KPI name"
                    required
                    disabled={saving}
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="measurementType">Measurement Type *</label>
                  <select
                    id="measurementType"
                    value={formData.measurementType}
                    onChange={(e) => handleInputChange('measurementType', e.target.value)}
                    required
                    disabled={saving}
                  >
                    <option value="">Select measurement type</option>
                    {measurementTypes.map((type) => (
                      <option key={type} value={type}>{type}</option>
                    ))}
                  </select>
                </div>

                <div className="form-group">
                  <label htmlFor="type">Time Period Type *</label>
                  <select
                    id="type"
                    value={formData.type}
                    onChange={(e) => handleInputChange('type', e.target.value)}
                    required
                    disabled={saving}
                  >
                    <option value="">Select time period type</option>
                    {VALID_KPI_TIME_TYPES.map(type => (
                      <option key={type} value={type}>
                        {type} ({KPI_TIME_TYPES[type].abbreviation})
                      </option>
                    ))}
                  </select>
                  {formData.type && KPI_TIME_TYPES[formData.type] && (
                    <p style={{
                      fontSize: '0.85rem',
                      color: 'rgba(255, 255, 255, 0.6)',
                      marginTop: '5px',
                      fontStyle: 'italic'
                    }}>
                      {KPI_TIME_TYPES[formData.type].description}
                    </p>
                  )}
                </div>

                <div className="form-group">
                  <label htmlFor="description">Description</label>
                  <textarea
                    id="description"
                    value={formData.description}
                    onChange={(e) => handleInputChange('description', e.target.value)}
                    placeholder="Optional description of what this KPI measures"
                    rows={3}
                    disabled={saving}
                  />
                </div>

                <div className="form-actions">
                  <button 
                    type="submit" 
                    className="save-btn"
                    disabled={saving || !formData.kpiName.trim() || !formData.measurementType || !formData.type}
                  >
                    {saving ? '⏳ Saving...' : editingKPI ? '💾 Update KPI' : '✨ Create KPI'}
                  </button>
                  <button 
                    type="button" 
                    className="cancel-btn"
                    onClick={handleCancel}
                    disabled={saving}
                  >
                    {editingKPI ? 'Cancel Edit' : 'Clear Form'}
                  </button>
                </div>
              </form>
            </div>
        </div>
      </div>
    </div>
  );
};

export default KPIManagementModal;